import { Koi } from '@ksyos/koi';
import type { IAsset } from '../assets';
import { BezoekAanSpecialistOption } from '../../enums/bezoekAanSpecialistOption';
import { ConclusieDiagnoseOption } from '../../enums/conclusieDiagnoseOption';
import { YesOrNo } from '../../enums/yesOrNo';
import { assets } from '../assets';
import { emailSchema } from '../schemas/emailSchema';

export const huidkankerOptions = ['Basaalcelcarcinoom', 'Plaveiselcelcarcinoom', 'Melanoom', 'Anders'];

export enum DermatologyPolicies {
    anders = 'Anders',
    bioptDoorHuisarts = 'Biopt/excisie door huisarts',
    expectatiefBeleid = 'Expectatief beleid',
    verwijzingNaarDermatoloog = 'Verwijzing naar dermatoloog',
}

export enum DermatologyUrgencies {
    oneDay = 'Binnen 1 dag',
    oneMonth = 'Binnen 1 maand',
    oneWeek = 'Binnen 1 week',
    sixMonths = 'Binnen 6 maanden',
    threeMonths = 'Binnen 3 maanden',
}

export const conditionStartedOptions = [
    'Vandaag/vannacht',
    'Gisteren',
    'Afgelopen dagen',
    'Vorige week',
    'Langer dan een week geleden',
];
export const conditionChangedOptions = ['Verslechterd', 'Verbeterd', 'Gelijk gebleven'];
export const conditionColorChangedOptions = ['Het is roder geworden', 'Het is lichter geworden', 'Gelijk gebleven'];
export const conditionSizeChangedOptions = ['Het is groter geworden', 'Het is kleiner geworden', 'Gelijk gebleven'];
export const yesNoOptions = ['Ja', 'Nee'];

export interface IDermaOthProcedureCustomControlData {
    condition: {
        anders: string | null;
        jeukt: boolean;
        pijn: boolean;
    };
    conditionBefore: string | null;
    conditionBeforeRemark: string | null;
    conditionChanged: string | null;
    conditionColorChanged: string | null;
    conditionSizeChanged: string | null;
    conditionStarted: string | null;
    extraQuestion: string | null;
    files: IAsset[];
    treatingCondition: string | null;
    treatingConditionRemark: string | null;
}

export interface IAltoDermaOthProcedureCustomControlData extends IDermaOthProcedureCustomControlData {
    patient: {
        bsn: string | null;
        isSomeoneElse: boolean | null;
        someoneElseName: string | null;
    };
}

export const dermaOthFinalProcedure = Koi.object().keys({
    condition: Koi.object({
        anders: Koi.string().max(550).allow(null),
        jeukt: Koi.boolean(),
        pijn: Koi.boolean(),
    }),
    conditionBefore: Koi.string()
        .valid(...yesNoOptions, 'Weet ik niet zeker')
        .allow(null),
    conditionBeforeRemark: Koi.string().max(550).allow(null),
    conditionChanged: Koi.string()
        .valid(...conditionChangedOptions)
        .allow(null),
    conditionColorChanged: Koi.string()
        .valid(...conditionColorChangedOptions)
        .allow(null),
    conditionSizeChanged: Koi.string()
        .valid(...conditionSizeChangedOptions)
        .allow(null),
    conditionStarted: Koi.string()
        .valid(...conditionStartedOptions)
        .allow(null),
    extraQuestion: Koi.string().max(550).allow(null),
    files: assets.min(2),
    treatingCondition: Koi.string()
        .valid(...yesNoOptions, 'Weet ik niet')
        .allow(null),
    treatingConditionRemark: Koi.string().max(550).allow(null),
});

export const altoDermaOthFinalProcedure = Koi.object({
    patient: {
        bsn: Koi.string().required(),
        isSomeoneElse: Koi.bool(),
        someoneElseName: Koi.when('isSomeoneElse', {
            is: true,
            otherwise: Koi.string().allow(null),
            then: Koi.string().required(),
        }),
    },
}).concat(dermaOthFinalProcedure);

const dermaAppPhotoAssetIds = Koi.object().pattern(/^photo\d+AssetId$/, Koi.string().uuid().allow(null).optional());

const dermaDiagnosticReportCommonFields = Koi.object().keys({
    bevindingen: Koi.string().max(1000),
    kwaliteit_fotos_voldoende: Koi.koi().enum(YesOrNo).allow(null),
    omschrijving_beleid: Koi.string().max(1000).allow(null),
});

export const dermaCV1FinalRequest = Koi.object()
    .keys({
        behandeling_geprobeerd: Koi.boolean().allow(null),
        behandeling_geprobeerd_remark: Koi.string().max(1000).allow(null),
        beschrijving_klachten: Koi.string().max(1000).allow(null),
        bewezen_allergie: Koi.boolean().allow(null),
        bewezen_allergie_remark: Koi.string().max(1000).allow(null),
        chronisch: Koi.boolean().allow(null),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        duur_klachten: Koi.string().max(1000).allow(null),
        familiaire_huidziekten: Koi.boolean().allow(null),
        familiaire_huidziekten_remark: Koi.string().max(1000).allow(null),
        lichaamsdeel: Koi.string().max(1000).allow(null),
        recidief: Koi.boolean().allow(null),
        symptomen: Koi.object().keys({
            anders: Koi.string().allow(null),
            bloeden: Koi.boolean(),
            cosmetisch: Koi.boolean(),
            jeuk: Koi.boolean(),
            pijn: Koi.boolean(),
        }),
        toelichting: Koi.string().max(1000).allow(null),
        vraag: Koi.string().max(1000),
    })
    .concat(dermaAppPhotoAssetIds);

export const dermaSnelCV1FinalRequest = dermaCV1FinalRequest;

export const dermaCV1DraftRequest = dermaCV1FinalRequest.concat(
    Koi.object({
        vraag: Koi.any().allow(null),
    })
);

export const dermaSnelCV1DraftRequest = dermaCV1DraftRequest;

export const dermaCV1FinalDiagnosticReport = dermaDiagnosticReportCommonFields.concat(
    Koi.object().keys({
        beleid: Koi.when('bezoek_aan_specialist_nu_nodig', {
            is: BezoekAanSpecialistOption.zieBeleid,
            otherwise: Koi.koi().enum(DermatologyPolicies).allow(null),
            then: Koi.koi().enum(DermatologyPolicies).disallow(DermatologyPolicies.expectatiefBeleid).allow(null),
        }),
        beleidAndersText: Koi.when('beleid', {
            is: DermatologyPolicies.anders,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
        bezoek_aan_specialist_nu_nodig: Koi.koi().enum(BezoekAanSpecialistOption),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
        toelichting: Koi.string().max(10000).allow(null),
        urgentie: Koi.when('bezoek_aan_specialist_nu_nodig', {
            is: BezoekAanSpecialistOption.ja,
            otherwise: Koi.valid(null),
            then: Koi.when('beleid', {
                is: Koi.not(DermatologyPolicies.expectatiefBeleid),
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(DermatologyUrgencies).allow(null),
            }),
        }),
    })
);

export const dermaSnelCV1FinalDiagnosticReport = dermaCV1FinalDiagnosticReport;

export const dermaCV1DraftDiagnosticReport = dermaCV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        beleidAndersText: Koi.any().allow(null),
        bevindingen: Koi.any().allow(null),
        bezoek_aan_specialist_nu_nodig: Koi.any().allow(null),
        conclusie_diagnose: Koi.any().allow(null),
        urgentie: Koi.any().allow(null),
    })
);

export const dermaSnelCV1DraftDiagnosticReport = dermaCV1DraftDiagnosticReport;

export const dermaCscV1FinalRequest = Koi.object()
    .keys({
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        huidkanker_familie: Koi.boolean().allow(null),
        huidkanker_familie_option: Koi.when('huidkanker_familie', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().valid(...huidkankerOptions),
        }),
        huidkanker_familie_option_anders: Koi.when('huidkanker_familie_option', {
            is: 'Anders',
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
        huidkanker_voorgeschiedenis: Koi.boolean().allow(null),
        huidkanker_voorgeschiedenis_option: Koi.when('huidkanker_voorgeschiedenis', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.string().valid(...huidkankerOptions),
        }),
        huidkanker_voorgeschiedenis_option_anders: Koi.when('huidkanker_voorgeschiedenis_option', {
            is: 'Anders',
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
        klachten: Koi.object().keys({
            anders: Koi.string().max(1000).allow(null),
            bloeden: Koi.boolean(),
            cosmetisch: Koi.boolean(),
            jeuk: Koi.boolean(),
            pijn: Koi.boolean(),
        }),
        klachten_verandering_sinds: Koi.string().max(1000).allow(null),
        lichaamsdeel: Koi.string().max(1000).allow(null),
        moedervlek: Koi.string().valid('Nieuw', 'Bestaand').allow(null),

        toelichting: Koi.string().max(1000).allow(null),
        veranderingen_moedervlek: Koi.object().keys({
            grootte: Koi.boolean(),
            kleur: Koi.boolean(),
            vorm: Koi.boolean(),
        }),
        vraag: Koi.object().keys({
            anders: Koi.string().max(1000).allow(null),
            diagnostiek_en_behandeling: Koi.boolean(),
            expectatief_beleid: Koi.boolean(),
            verdacht_van_kwaadaardigheid: Koi.boolean(),
        }),
        werkdiagnose: Koi.object().keys({
            atypische_naevus: Koi.boolean(),
            naevus_naevocellularis: Koi.boolean(),
            verdenking_melanoom: Koi.boolean(),
        }),
    })
    .concat(dermaAppPhotoAssetIds);

export const dermaSnelCscV1FinalRequest = dermaCscV1FinalRequest;

export const dermaCscV1DraftRequest = dermaCscV1FinalRequest;

export const dermaSnelCscV1DraftRequest = dermaSnelCscV1FinalRequest;

export const dermaCscV1FinalDiagnosticReport = dermaDiagnosticReportCommonFields.concat(
    Koi.object().keys({
        asymmetrie: Koi.number().min(0).max(2).allow(null),
        beleid: Koi.when('bezoek_aan_specialist_nodig', {
            is: BezoekAanSpecialistOption.zieBeleid,
            otherwise: Koi.koi().enum(DermatologyPolicies).allow(null),
            then: Koi.koi().enum(DermatologyPolicies).disallow(DermatologyPolicies.expectatiefBeleid).allow(null),
        }),
        beleid_anders: Koi.when('beleid', {
            is: DermatologyPolicies.anders,
            otherwise: Koi.valid(null),
            then: Koi.string().max(255).required(),
        }),
        bezoek_aan_specialist_nodig: Koi.koi().enum(BezoekAanSpecialistOption),
        border: Koi.number().min(0).max(8).allow(null),
        color: Koi.number().min(0).max(6).allow(null),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
        differentialStructures: Koi.number().min(0).max(5).allow(null),
        toelichting: Koi.string().max(10000).allow(null),
        urgentie: Koi.when('bezoek_aan_specialist_nodig', {
            is: BezoekAanSpecialistOption.ja,
            otherwise: Koi.valid(null),
            then: Koi.when('beleid', {
                is: Koi.not(DermatologyPolicies.expectatiefBeleid),
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(DermatologyUrgencies).allow(null),
            }),
        }),
        werkdiagnose: {
            anders: Koi.string().max(255).allow(null),
            atypische_naevus: Koi.boolean(),
            naevus_naevocellularis: Koi.boolean(),
            verdenking_melanoom: Koi.boolean(),
        },
    })
);

export const dermaSnelCscV1FinalDiagnosticReport = dermaCscV1FinalDiagnosticReport;

export const dermaCscV1DraftDiagnosticReport = dermaCscV1FinalDiagnosticReport.concat(
    Koi.object({
        bevindingen: Koi.any().allow(null),
        bezoek_aan_specialist_nodig: Koi.any().allow(null),
        conclusie_diagnose: Koi.any().allow(null),
        urgentie: Koi.any().allow(null),
    })
);

export const dermaSnelCscV1DraftDiagnosticReport = dermaCscV1DraftDiagnosticReport;

export const dermaCwondV1FinalRequest = Koi.object()
    .keys({
        algemene_voorgeschiedenis: Koi.string().max(1000).allow(null),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption).allow(null),
        duur_ulcus: Koi.string().max(1000).allow(null),
        enkelarm_index: Koi.boolean().allow(null),
        huidig_wondbeleid: Koi.string().max(1000).allow(null),
        ontstaanswijze_ulcus: Koi.string().max(1000).allow(null),
        teendruk: Koi.boolean().allow(null),
        toelichting: Koi.string().max(1000).allow(null),
        veneuze_duplex: Koi.boolean().allow(null),
        vraag: Koi.string().max(1000),
    })
    .concat(dermaAppPhotoAssetIds);

export const dermaCwondV1DraftRequest = dermaCwondV1FinalRequest.concat(
    Koi.object({
        vraag: Koi.any().allow(null),
    })
);

export const dermaCwondV1FinalDiagnosticReport = dermaDiagnosticReportCommonFields.concat(
    Koi.object().keys({
        beleid: Koi.when('bezoek_aan_specialist_nodig', {
            is: BezoekAanSpecialistOption.zieBeleid,
            otherwise: Koi.koi().enum(DermatologyPolicies).allow(null),
            then: Koi.koi().enum(DermatologyPolicies).disallow(DermatologyPolicies.expectatiefBeleid).allow(null),
        }),
        beleidAndersText: Koi.when('beleid', {
            is: DermatologyPolicies.anders,
            otherwise: Koi.valid(null),
            then: Koi.string().max(1000),
        }),
        bezoek_aan_specialist_nodig: Koi.koi().enum(BezoekAanSpecialistOption),
        conclusie_diagnose: Koi.koi().enum(ConclusieDiagnoseOption),
        toelichting: Koi.string().max(10000).allow(null),
        urgentie: Koi.when('bezoek_aan_specialist_nodig', {
            is: BezoekAanSpecialistOption.ja,
            otherwise: Koi.valid(null),
            then: Koi.when('beleid', {
                is: Koi.not(DermatologyPolicies.expectatiefBeleid),
                otherwise: Koi.valid(null),
                then: Koi.koi().enum(DermatologyUrgencies).allow(null),
            }),
        }),
    })
);

export const dermaCwondV1DraftDiagnosticReport = dermaCwondV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        beleidAndersText: Koi.any().allow(null),
        bevindingen: Koi.any().allow(null),
        bezoek_aan_specialist_nodig: Koi.any().allow(null),
        conclusie_diagnose: Koi.any().allow(null),
        urgentie: Koi.any().allow(null),
    })
);

export const dermaOthV1FinalRequest = Koi.object().keys({
    contactgegevens_patient: Koi.object().keys({
        emailadres: emailSchema,
    }),
    identifierSocialSecurity: Koi.string().elfproef(),
    patientHasDigid: Koi.koi().enum(YesOrNo),
    toestemming: Koi.boolean(),
});

export const dermaOthV1DraftRequest = dermaOthV1FinalRequest.concat(
    Koi.object({
        contactgegevens_patient: Koi.object().keys({
            emailadres: emailSchema.allow(null),
        }),
        patientHasDigid: Koi.koi().enum(YesOrNo).allow(null),
    })
);

export const bevindingenOptions = ['Normaal', 'Afwijkend', 'Niet te beoordelen'];

export const dermaOthV1FinalDiagnosticReport = Koi.object().keys({
    bevindingen: Koi.string().valid(...bevindingenOptions),
    toelichting: Koi.string().max(1000).allow(null),
});

export const dermaOthV1DraftDiagnosticReport = dermaOthV1FinalDiagnosticReport.concat(
    Koi.object().keys({
        bevindingen: Koi.any().allow(null),
    })
);
